import { dungeonService } from './DungeonService';
import {
  GroundItem,
  GroundItemMode,
  FlyingItem,
  ItemType,
  PickupNotification,
} from '../types/inventory.types';
import { useInventoryStore } from '../store/inventorySlice';
import { ITEMS } from '../data/inventory.config';
import { isItemUseful, getUnlockedSpellTargets } from '../utils/item.utils';
import { findDropCell } from '../utils/map.utils';
import { learnSpellFromGrimoire } from './SpellMasteryService';
import { EFFECT_HANDLERS } from '../effect-system/effectHandlers';
import { checkEffectConditions } from '../effect-system/conditionChecker';
import { EffectContext } from '../types/spell.types';
import { DungeonEntity } from '../entities/DungeonEntity';
import { PlayerEntity } from '../entities/PlayerEntity';
import { tileWidth, tileHeight } from '../data/tileset.config';
import { msToTicks } from '../utils/timing.utils';
import { v4 as uuidv4 } from 'uuid';
import { audioService } from './AudioService';
import { netService } from './NetService';
import {
  makeThrowItem,
  makeCommonItemAdd,
  makeCommonPickupRequest,
  makeCommonItemRemove,
} from '../net/sessionEvents';
import { setCoopGround } from '../net/reconnect';
import { useGameStore } from '../store/gameSlice';
import type { SavedGroundItem } from '../types/save.types';

const PICKUP_ANIM_MS = 250;
const PICKUP_NOTIF_TICKS = 90;
const PICKUP_NOTIF_STAGGER_TICKS = msToTicks(200);

export class InventoryService {
  public dungeonService!: typeof dungeonService;
  private groundItemMap = new Map<string, GroundItem[]>();
  private flyingItems: FlyingItem[] = [];
  private pickupNotifications: PickupNotification[] = [];
  /** Remaining ticks before the next pickup notification is allowed to appear. */
  private nextNotifSlot = 0;
  private persistTimer: number | null = null;
  private pendingPickupRequests = new Set<string>();

  private tileKey(x: number, y: number): string {
    return `${x},${y}`;
  }

  public dropItem(
    x: number,
    y: number,
    itemId: string,
    quantity = 1,
    validatePlacement = true,
    mode: GroundItemMode = 'possessed',
    dropperId?: string,
  ): void {
    if (!ITEMS[itemId]) {
      return;
    }
    let dropX = x;
    let dropY = y;
    if (validatePlacement) {
      const cell = findDropCell(x, y);
      if (!cell) {
        return;
      }
      dropX = cell.x;
      dropY = cell.y;
    }
    const key = this.tileKey(dropX, dropY);
    const item: GroundItem = {
      id: uuidv4(),
      itemId,
      x: dropX,
      y: dropY,
      quantity,
      mode,
      dropperId,
    };
    const list = this.groundItemMap.get(key);
    if (list) {
      list.push(item);
    } else {
      this.groundItemMap.set(key, [item]);
    }
    this.dungeonService.markNeedsRender();
    this.schedulePossessedPersist();

    if (mode !== 'possessed') {
      return;
    }
    // If this drop happens within the player's pickup reach, pick it up
    // immediately. Otherwise the player would have to move once before
    // adjacent drops were collected.
    const player = this.dungeonService.getPlayer();
    if (player) {
      const dx = dropX - player.positionX;
      const dy = dropY - player.positionY;
      const reach = player.stats.pickupReach;
      if (dx * dx + dy * dy <= reach * reach) {
        this.pickupItemsInRange(player, reach);
      }
    }
  }

  public getItemsAt(x: number, y: number): GroundItem[] {
    return this.groundItemMap.get(this.tileKey(x, y)) ?? [];
  }

  public getAllGroundItems(): GroundItem[] {
    const result: GroundItem[] = [];
    this.groundItemMap.forEach((list) => result.push(...list));
    return result;
  }

  public removeGroundItem(id: string): void {
    for (const [key, list] of Array.from(this.groundItemMap)) {
      const idx = list.findIndex((i: GroundItem) => i.id === id);
      if (idx !== -1) {
        list.splice(idx, 1);
        if (list.length === 0) {
          this.groundItemMap.delete(key);
        }
        this.dungeonService.markNeedsRender();
        this.schedulePossessedPersist();
        return;
      }
    }
  }

  /**
   * Move every ground item from one tile to another, preserving item ids (no
   * pickup side-effect, no new uuid). Used by DynamicPlacementService to clear
   * loot off a cell about to receive a location/wall.
   */
  public moveGroundItems(
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
  ): void {
    const fromKey = this.tileKey(fromX, fromY);
    const items = this.groundItemMap.get(fromKey);
    if (!items || items.length === 0) {
      return;
    }
    this.groundItemMap.delete(fromKey);
    for (const item of items) {
      item.x = toX;
      item.y = toY;
    }
    const toKey = this.tileKey(toX, toY);
    const dest = this.groundItemMap.get(toKey);
    if (dest) {
      dest.push(...items);
    } else {
      this.groundItemMap.set(toKey, items);
    }
    this.dungeonService.markNeedsRender();
    this.schedulePossessedPersist();
  }

  /**
   * Pick up every ground item now within the player's reach. Call after ground
   * items move on their own (e.g. relocation by DynamicPlacementService when a
   * block/location spawns on them) so loot shoved into reach is collected
   * without waiting for the next player step.
   */
  public triggerGroundPickup(): void {
    const player = this.dungeonService.getPlayer();
    if (!player) {
      return;
    }
    this.pickupItemsInRange(player, player.stats.pickupReach);
  }

  public pickupItemsInRange(player: PlayerEntity, pickupRange: number): void {
    const isLocal =
      netService.role === 'offline' ||
      player.id === this.dungeonService.localPlayerId;
    if (!isLocal) {
      return;
    }

    const px = player.positionX;
    const py = player.positionY;
    const r2 = pickupRange * pickupRange;
    const scan = Math.ceil(pickupRange);

    for (let dx = -scan; dx <= scan; dx++) {
      for (let dy = -scan; dy <= scan; dy++) {
        if (dx * dx + dy * dy > r2) {
          continue;
        }
        const key = this.tileKey(px + dx, py + dy);
        const list = this.groundItemMap.get(key);
        if (!list || list.length === 0) {
          continue;
        }
        const items = [...list];
        const count = items.length;
        const SPREAD = tileWidth * 0.18;
        const remaining: GroundItem[] = [];
        let pickedAny = false;
        for (let i = 0; i < count; i++) {
          const item = items[i];
          if (item.mode !== 'common') {
            // Possessed item: existing leftover machinery.
            const angle = count > 1 ? (i / count) * Math.PI * 2 : 0;
            const ox = count > 1 ? Math.cos(angle) * SPREAD : 0;
            const oy = count > 1 ? -Math.sin(angle) * SPREAD : 0;
            const leftover = useInventoryStore
              .getState()
              .addItem(item.itemId, item.quantity);
            if (leftover < item.quantity) {
              pickedAny = true;
              this.startFlyingItem(
                item,
                item.quantity - leftover,
                player,
                ox,
                oy,
              );
            }
            if (leftover > 0) {
              remaining.push({ ...item, quantity: leftover });
            }
          } else {
            // Common (host-authoritative) item.
            if (!this.canPickupCommon(item, player)) {
              remaining.push(item);
            } else if (
              !useInventoryStore
                .getState()
                .hasRoomFor(item.itemId, item.quantity)
            ) {
              remaining.push(item);
            } else {
              if (netService.role === 'offline') {
                this.applyCommonItemRemoval(item.id, player.id);
                pickedAny = true;
              } else if (netService.isHost()) {
                this.applyCommonItemRemoval(item.id, player.id);
                netService.session?.broadcast(
                  'reliable',
                  makeCommonItemRemove(item.id, player.id),
                );
                pickedAny = true;
              } else if (netService.isClient()) {
                if (!this.pendingPickupRequests.has(item.id)) {
                  this.pendingPickupRequests.add(item.id);
                  netService.session?.broadcast(
                    'reliable',
                    makeCommonPickupRequest(item.id),
                  );
                }
                remaining.push(item);
              }
            }
          }
        }
        // Rebuild the tile list from items we chose to keep. Items removed
        // via applyCommonItemRemoval are already gone from the map; setting
        // the key to `remaining` (which excludes them) stays consistent.
        const anythingRemoved = remaining.length < count;
        if (!pickedAny && !anythingRemoved) {
          continue;
        }
        if (remaining.length > 0) {
          this.groundItemMap.set(key, remaining);
        } else {
          this.groundItemMap.delete(key);
        }
        this.dungeonService.markNeedsRender();
      }
    }
    this.schedulePossessedPersist();
  }

  public throwItem(itemId: string, quantity: number): void {
    const player = this.dungeonService.getPlayer();
    if (!player) {
      return;
    }
    const removed = useInventoryStore.getState().removeItem(itemId, quantity);
    if (removed <= 0) {
      return;
    }
    const cell = findDropCell(player.positionX, player.positionY) ?? {
      x: player.positionX,
      y: player.positionY,
    };
    if (netService.isClient()) {
      netService.session?.broadcast(
        'reliable',
        makeThrowItem(itemId, removed, cell.x, cell.y),
      );
      return;
    }
    const item: GroundItem = {
      id: uuidv4(),
      itemId,
      x: cell.x,
      y: cell.y,
      quantity: removed,
      mode: 'common',
      dropperId: player.id,
    };
    this.addCommonItemRaw(item);
    if (netService.isHost()) {
      netService.session?.broadcast(
        'reliable',
        makeCommonItemAdd(item.id, itemId, removed, cell.x, cell.y, player.id),
      );
    }
  }

  public addCommonItemRaw(item: GroundItem): void {
    const key = this.tileKey(item.x, item.y);
    const list = this.groundItemMap.get(key);
    if (list) {
      list.push(item);
    } else {
      this.groundItemMap.set(key, [item]);
    }
    this.dungeonService.markNeedsRender();
  }

  private findCommonItem(id: string): GroundItem | undefined {
    for (const list of this.groundItemMap.values()) {
      const item = list.find((i) => i.id === id && i.mode === 'common');
      if (item) {
        return item;
      }
    }
    return undefined;
  }

  public getCommonItemById(id: string): GroundItem | undefined {
    return this.findCommonItem(id);
  }

  public applyCommonItemRemoval(id: string, byPlayerId: string): void {
    const item = this.findCommonItem(id);
    if (!item) {
      return;
    }
    const byEntity = this.dungeonService.getEntityById(byPlayerId);
    if (byEntity) {
      this.startFlyingItem(item, item.quantity, byEntity);
    }
    this.removeGroundItem(id);
    this.pendingPickupRequests.delete(id);
    if (byPlayerId === this.dungeonService.localPlayerId) {
      useInventoryStore.getState().addItem(item.itemId, item.quantity);
    }
  }

  private canPickupCommon(item: GroundItem, player: PlayerEntity): boolean {
    return item.dropperId !== player.id || item.armed === true;
  }

  public armCommonItems(): void {
    for (const list of this.groundItemMap.values()) {
      for (const item of list) {
        if (item.mode !== 'common' || item.armed || !item.dropperId) {
          continue;
        }
        const dropper = this.dungeonService.getEntityById(item.dropperId);
        if (!(dropper instanceof PlayerEntity)) {
          item.armed = true;
          continue;
        }
        const dx = item.x - dropper.positionX;
        const dy = item.y - dropper.positionY;
        const reach = dropper.stats.pickupReach;
        if (dx * dx + dy * dy > reach * reach) {
          item.armed = true;
        }
      }
    }
  }

  private startFlyingItem(
    source: GroundItem,
    quantity: number,
    entity: DungeonEntity,
    startOffsetX = 0,
    startOffsetY = 0,
  ): void {
    const absX = source.x * tileWidth + startOffsetX;
    const absY = source.y * tileHeight + startOffsetY;
    this.flyingItems.push({
      id: uuidv4(),
      entity,
      itemId: source.itemId,
      quantity,
      startX: absX,
      startY: absY,
      currentX: absX,
      currentY: absY,
      progress: 0,
      duration: msToTicks(PICKUP_ANIM_MS),
    });
    this.dungeonService.markNeedsRender();
  }

  public updateFlyingItems(deltaTime: number): void {
    // Stagger countdown ticks down regardless, so consecutive bursts spread out.
    this.nextNotifSlot = Math.max(0, this.nextNotifSlot - deltaTime);

    if (
      this.flyingItems.length === 0 &&
      this.pickupNotifications.length === 0
    ) {
      return;
    }
    const toRemove: number[] = [];
    for (let i = 0; i < this.flyingItems.length; i++) {
      const fi = this.flyingItems[i];
      fi.progress += deltaTime / fi.duration;
      if (fi.progress >= 1) {
        toRemove.push(i);
        this.pickupNotifications.push({
          id: uuidv4(),
          itemId: fi.itemId,
          quantity: fi.quantity,
          worldX: fi.entity.absoluteX,
          worldY: fi.entity.absoluteY,
          elapsed: 0,
          delay: this.nextNotifSlot,
          duration: PICKUP_NOTIF_TICKS,
        });
        this.nextNotifSlot += PICKUP_NOTIF_STAGGER_TICKS;
      } else {
        const t = fi.progress * fi.progress;
        fi.currentX = fi.startX + (fi.entity.absoluteX - fi.startX) * t;
        fi.currentY = fi.startY + (fi.entity.absoluteY - fi.startY) * t;
      }
    }
    for (let i = toRemove.length - 1; i >= 0; i--) {
      this.flyingItems.splice(toRemove[i], 1);
    }

    const notifToRemove: number[] = [];
    for (let i = 0; i < this.pickupNotifications.length; i++) {
      const n = this.pickupNotifications[i];
      n.elapsed += deltaTime;
      if (n.elapsed >= n.delay + n.duration) {
        notifToRemove.push(i);
      }
    }
    for (let i = notifToRemove.length - 1; i >= 0; i--) {
      this.pickupNotifications.splice(notifToRemove[i], 1);
    }

    if (
      toRemove.length > 0 ||
      this.flyingItems.length > 0 ||
      notifToRemove.length > 0 ||
      this.pickupNotifications.length > 0
    ) {
      this.dungeonService.markNeedsRender();
    }
  }

  public getPickupNotifications(): PickupNotification[] {
    return this.pickupNotifications;
  }

  public getFlyingItems(): FlyingItem[] {
    return this.flyingItems;
  }

  public useConsumable(itemId: string): boolean {
    const def = ITEMS[itemId];
    if (!def || def.type !== ItemType.CONSUMABLE) {
      return false;
    }
    // Usefulness pre-check (e.g. a grimoire whose spell is already learned:
    // no possible use). Item not consumed, so callers can react.
    if (!isItemUseful(def)) {
      return false;
    }
    audioService.playSfx(
      getUnlockedSpellTargets(def).length > 0 ? 'learn-spell' : 'item-use',
    );
    const player = this.dungeonService.getPlayer();
    if (!player) {
      // Hub / no active run: only player-independent grimoire unlocks can run
      // (no dungeon player to build an effect context for). A consumable with
      // no spell-unlock effect (heal pill, etc.) cannot be used here.
      const spellTargets = getUnlockedSpellTargets(def);
      if (spellTargets.length === 0) {
        return false;
      }
      spellTargets.forEach(learnSpellFromGrimoire);
      return true;
    }
    const ctx: EffectContext = {
      caster: player,
      direction: player.direction,
      affectedCells: [],
    };
    for (const effect of def.effects ?? []) {
      if (!checkEffectConditions(player, effect.conditions)) {
        continue;
      }
      EFFECT_HANDLERS[effect.handler]?.(ctx, { ...effect.params });
    }
    this.dungeonService.markNeedsRender();
    return true;
  }

  public clear(): void {
    if (this.persistTimer !== null) {
      window.clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
    this.groundItemMap.clear();
    this.flyingItems = [];
    this.pickupNotifications = [];
    this.nextNotifSlot = 0;
    this.pendingPickupRequests.clear();
  }

  private schedulePossessedPersist(): void {
    if (!netService.isClient()) {
      return;
    }
    if (this.persistTimer !== null) {
      return;
    }
    this.persistTimer = window.setTimeout(() => {
      this.persistTimer = null;
      const possessed: SavedGroundItem[] = this.getAllGroundItems()
        .filter((gi) => gi.mode === 'possessed')
        .map((gi) => ({
          itemId: gi.itemId,
          x: gi.x,
          y: gi.y,
          quantity: gi.quantity,
          mode: 'possessed',
        }));
      setCoopGround(useGameStore.getState().currentFloor, possessed);
    }, 500);
  }
}
